import { ChangeDetectionStrategy, Component, computed, effect, input, signal } from '@angular/core';
import { AbstractControl } from '@angular/forms';

/**
 * Lays out a DS 2.0 form field (label, box, message) and holds its state. The pieces inside read it:
 * `c-field-message` shows the error or help text, `cFieldBox` puts the state classes on the box.
 * Style the label, box and icons with the mixins in `theme/form-field`.
 */
@Component({
  selector: 'c-form-field',
  template: '<ng-content />',
  styleUrl: './form-field.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormFieldComponent {
  readonly control = input<AbstractControl | null | undefined>();
  /** Lets a form hold errors back, e.g. until it's submitted. A control also has to be touched to show one. */
  readonly showErrors = input(true);
  /** Shows the box focused while focus is outside it, e.g. while a picker modal is open. */
  readonly focused = input(false);

  // Bumped on every control event, so the computed state below follows the control under OnPush.
  private readonly controlVersion = signal(0);

  readonly hasErrors = computed(() => {
    const control = this.trackedControl();
    return !!(this.showErrors() && control?.errors && control.touched);
  });
  readonly errors = computed(() => this.trackedControl()?.errors);
  readonly value = computed(() => this.trackedControl()?.value);

  constructor() {
    effect((onCleanup) => {
      const subscription = this.control()?.events.subscribe(() => this.controlVersion.update((version) => version + 1));
      onCleanup(() => subscription?.unsubscribe());
    });
  }

  private trackedControl(): AbstractControl | null | undefined {
    this.controlVersion();
    return this.control();
  }
}
